import React, {useEffect, useState} from 'react';
import {Stack, Typography} from "@mui/material";
import {TogetherCustomModelOptions} from "../../api/json/CustomModelOptionsJson";
import {ModelSpecTogetherJson} from "../../api/json/ModelSpecJson"
import {ModelPlatform} from "../../api/enum/ModelPlatform"
import NumberInput from "../form/NumberInput"
import {AnyTemplateJson} from "../../api/json/TemplateJson"
import {TogetherTrainingType} from "../../api/enum/TogetherTrainingType"
import {useProjectData} from "../../hooks/useProjectData"
import {suggestedLoraRank} from "../../library/LoraRank"

interface CustomModelFormTogetherProps {
    template: AnyTemplateJson
    baseModel: ModelSpecTogetherJson
    setOptions: (options: TogetherCustomModelOptions) => void
    setFormValid: (valid: boolean) => void
}

function CustomModelFormTogether({template, baseModel, setOptions, setFormValid}: CustomModelFormTogetherProps) {
    const {project} = useProjectData()
    const exampleCount = project?.stats.numberOfTrainingRows ?? 0
    const suggestedRank = suggestedLoraRank(exampleCount, baseModel.maxLoraRank ?? 64)

    const [numberOfEpochs, setNumberOfEpochs] = useState<number>(2)
    const [learningRate, setLearningRate] = useState<number>(0.0002)
    const [batchSize, setBatchSize] = useState<number>(baseModel.minBatchSize ?? 8)

    const [loraRank, setLoraRank] = useState<number>(suggestedRank)
    const [loraAlpha, setLoraAlpha] = useState<number>(suggestedRank)

    const useLoRA = baseModel.trainingType === TogetherTrainingType.LORA || baseModel.trainingType === TogetherTrainingType.LORA_LONG_CONTEXT

    const maxLoraRank = baseModel.maxLoraRank ?? 64

    useEffect(() => {
        const minBatchSize = baseModel.minBatchSize ?? 1
        const maxBatchSize = baseModel.maxBatchSize ?? 1024
        const clampedBatchSize = Math.max(Math.min(batchSize, maxBatchSize), minBatchSize)
        if (clampedBatchSize !== batchSize) setBatchSize(clampedBatchSize)

        const clampedRank = Math.min(loraRank, maxLoraRank)
        if (clampedRank !== loraRank) {
            setLoraRank(clampedRank)
            setLoraAlpha(clampedRank)
        }

        setOptions({
            platform: ModelPlatform.TOGETHER,
            templateId: template.id,
            baseModel: baseModel,
            baseModelName: baseModel.name,
            numberOfEpochs: numberOfEpochs,
            learningRate: learningRate,
            batchSize: clampedBatchSize,
            trainingType: baseModel.trainingType ?? TogetherTrainingType.LORA,
            loraRank: useLoRA ? clampedRank : null,
            loraAlpha: useLoRA ? loraAlpha : null,
            loraDropout: null,
        })
    }, [baseModel, numberOfEpochs, learningRate, batchSize, loraRank, loraAlpha])

    useEffect(() => {
        setFormValid(true)
    }, [])

    return (
        <>
            <NumberInput label="Number of Epochs"
                         helperText="The number of full cycles to go through the training dataset. Multiplies pricing."
                         min={1}
                         max={20}
                         value={numberOfEpochs}
                         onChange={setNumberOfEpochs}
            />
            <NumberInput label="Learning Rate"
                         min={0.00000001}
                         max={0.01}
                         value={learningRate}
                         onChange={setLearningRate}
                         helperText={learningRate ? learningRate.toExponential() : undefined}
            />
            <NumberInput label="Batch Size"
                         min={baseModel.minBatchSize ?? 1}
                         max={baseModel.maxBatchSize ?? 1024}
                         value={batchSize}
                         onChange={setBatchSize}
                         helperText={`Min: ${baseModel.minBatchSize ?? 1}, Max: ${baseModel.maxBatchSize ?? 1024}`}
            />

            {useLoRA &&
                <>
                    <Typography variant="subtitle1">LoRA Hyperparameters</Typography>
                    <NumberInput label="Rank"
                                 min={1}
                                 max={maxLoraRank}
                                 helperText={`Higher values lead to more precise training of the model's weights. Max ${maxLoraRank} for this model.`}
                                 value={loraRank}
                                 onChange={(value) => {
                                     const newAlpha = value * (loraAlpha / loraRank)
                                     setLoraRank(value)
                                     setLoraAlpha(newAlpha)
                                 }}
                    />

                    <Stack direction="row" spacing={2}>
                        <NumberInput label="Alpha"
                                     min={1}
                                     max={50000}
                                     helperText="Divide by rank to get scale factor."
                                     value={loraAlpha}
                                     onChange={setLoraAlpha}
                                     sx={{flex: 1}}
                        />
                        <NumberInput label="Scale Factor"
                                     min={0}
                                     max={10}
                                     value={loraAlpha / loraRank}
                                     onChange={newValue => {
                                         setLoraAlpha(loraRank * newValue)
                                     }}
                                     helperText="Factor to multiply weight changes before adding to model weights."
                                     sx={{flex: 1}}
                        />
                    </Stack>
                </>
            }
        </>
    );
}

export default CustomModelFormTogether;
